import { Grid } from '@mui/material';
import { LineSeriesType } from '@mui/x-charts';
import produce from 'immer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { useGlobalLocaleContext } from '../../../../context/contexts';
import { DataFieldWithDataType } from '../../../../types';
import { transformToTranslate } from '../../../../utils-display';
import { CHART_CONTENT_HEIGHT, CHART_TITLE_HEIGHT } from '../../../styles';
import { benchmarkColor, chartColorsHex } from '../../../theme';
import {
  LineChartComponentConfig,
  OverTimeBarChartComponentConfig,
} from '../../dashboards/types';
import { ChartErrorDisplay } from '../../errors/components/ChartErrorDisplay';
import { formatHierarchicalLabel } from '../../filter/utils-display';
import { TableViewMetrics } from '../../tableview/TableViewMetrics';
import { ChartComponentProps } from '../chart-types';
import { ChartDataFetchError } from '../ChartDataFetchError';
import { ChartTitle } from '../ChartTitle';
import {
  useChartWidth,
  useSyncGlobalDisplayState,
  useToggleToolsOnSegmentationChange,
} from '../hooks';
import { LineChart } from '../mui-charts';
import { CustomAxisTooltip } from '../mui-charts/CustomAxisTooltip';
import { toMuiChartsOverTimeData } from '../mui-charts/utils-display';
import { calcMarginBasedOnYAxisValues } from '../utils';
import { MarkWithLabel } from './CustomLabel';
import { useMetricQueries } from './hooks';
import { chartStyles } from './styles';
import { Tools } from './Tools';

type LineChartOverTimeProps = ChartComponentProps<
  LineChartComponentConfig | OverTimeBarChartComponentConfig,
  | 'manualDataRefetchCountHandle'
  | 'timeSliderState'
  | 'filtersState'
  | 'segmentationState'
  | 'showTools'
  | 'fullScreenHandle'
  | 'displayGlobalState'
>;

export const LineChartOverTime = (props: LineChartOverTimeProps) => {
  const {
    componentConfig,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    segmentationState,
    showTools,
    fullScreenHandle,
    displayGlobalState,
  } = props;

  const { title, queries, chartTypeConfig, colors } = componentConfig;
  const locale = useGlobalLocaleContext();
  const { t } = useTranslation();
  const formatLabel = (
    value: (string | null)[],
    dataField: DataFieldWithDataType
  ) => formatHierarchicalLabel(value, dataField, t(UNDEFINED_DISPLAY_KEY), t);

  const { chartContainerRef, chartWidth } = useChartWidth();

  const {
    data: { series, tableViewData, metricSql, xAxisConfig, yAxisConfig },
    displayHandle,
    metrics,
    filters,
    segmentations,
    timeSelection,
    error,
  } = useMetricQueries<LineSeriesType>(
    componentConfig.chartId,
    queries,
    timeSliderState,
    filtersState,
    segmentationState,
    manualDataRefetchCountHandle.current,
    chartTypeConfig,
    displayGlobalState,
    formatLabel,
    colors,
    t,
    toMuiChartsOverTimeData<LineSeriesType>,
    chartWidth
  );
  const [displayState, displayReducer] = displayHandle;

  useSyncGlobalDisplayState(displayGlobalState, displayReducer);
  useToggleToolsOnSegmentationChange(
    segmentations,
    segmentationState,
    displayReducer
  );

  if (error && error.length > 0) {
    return (
      <ChartErrorDisplay
        error={error[0]}
        variant="inline"
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  // TODO: missing xAxisConfig and yAxisConfig would benefit from a dedicated error handling
  if (!xAxisConfig || !yAxisConfig) {
    return (
      <ChartDataFetchError
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }
  const styles = chartStyles({
    seriesStyling: queries.reduce(
      (acc, q, i) => {
        const seriesId = series[i].id;
        if (seriesId) {
          acc[seriesId] = {
            // TODO: improve handling of benchmark through types
            dottedLine: q.isBenchmark ?? false,
            stroke: q.isBenchmark ? benchmarkColor : undefined,
          };
        }
        return acc;
      },
      {} as Record<string, { dottedLine: boolean; stroke: string | undefined }>
    ),
  });

  const firstXAxis = xAxisConfig[0];
  const bottomXAxis = produce(firstXAxis, (draft) => {
    draft.tickLabelStyle = {
      transform: firstXAxis.transform
        ? transformToTranslate(firstXAxis.transform)
        : undefined,
    };
  });
  const secondXAxis = xAxisConfig[1];
  const topXAxis = produce(secondXAxis, (draft) => {
    if (secondXAxis) {
      draft.tickLabelStyle = {
        transform: secondXAxis.transform
          ? transformToTranslate({
              ...secondXAxis.transform,
              y: -secondXAxis.transform.y,
              rotation: -secondXAxis.transform.rotation,
            })
          : undefined,
        fill: queries[1]?.isBenchmark
          ? benchmarkColor
          : (colors?.[secondXAxis.id as number] ??
            chartColorsHex[secondXAxis.id as number]),
      };
    }
  });
  const finalXAxisConfig = [bottomXAxis, ...(topXAxis ? [topXAxis] : [])];

  const leftYAxisValues =
    series[0]?.data?.map(
      (d) =>
        yAxisConfig[0]?.valueFormatter?.(d as number, { location: 'tick' }) ??
        String(d)
    ) ?? [];
  let rightYAxisTickValueBuffer = 0;
  if (yAxisConfig[1]) {
    const rightYAxisValues =
      series[1]?.data?.map(
        (d) =>
          yAxisConfig[1]?.valueFormatter?.(d as number, { location: 'tick' }) ??
          String(d)
      ) ?? [];
    rightYAxisTickValueBuffer = calcMarginBasedOnYAxisValues(rightYAxisValues);
  }
  const leftYAxisTickValueBuffer =
    calcMarginBasedOnYAxisValues(leftYAxisValues);
  return (
    <Grid item container direction="column" sx={{ height: '100%' }}>
      <Grid item container sx={{ height: CHART_TITLE_HEIGHT }}>
        <ChartTitle
          metrics={metrics}
          metricsSql={metricSql}
          title={title?.[locale.selected]}
          timeSelection={timeSelection}
          segmentations={segmentations}
          userFilters={filters}
        />
        {showTools ? (
          <Tools
            chartRef={chartContainerRef}
            displayHandle={displayHandle}
            fullScreenHandle={fullScreenHandle}
          />
        ) : null}
      </Grid>
      <Grid
        item
        container
        ref={chartContainerRef}
        sx={{ height: CHART_CONTENT_HEIGHT }}
      >
        {displayState.showTableView ? (
          <TableViewMetrics
            tableData={tableViewData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
          />
        ) : (
          <LineChart
            sx={styles}
            title={title?.[locale.selected]}
            xAxis={finalXAxisConfig}
            topAxis={topXAxis ? String(topXAxis.id) : null}
            yAxis={yAxisConfig}
            rightAxis={yAxisConfig?.[1]?.id as string | undefined}
            series={series.map((s) => ({ ...s, connectNulls: true }))}
            margin={{
              right: 50 + rightYAxisTickValueBuffer,
              left: 50 + leftYAxisTickValueBuffer,
              top: 40 + (topXAxis?.height ? 20 : 0),
              bottom: bottomXAxis.height ?? 0,
            }}
            slotProps={{
              popper: { container: () => fullScreenHandle.node.current },
              legend: {
                hidden: !displayState.showLegend,
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                padding: {
                  top: 60,
                },
                itemMarkWidth: 20,
                itemMarkHeight: 2,
                markGap: 5,
                itemGap: 5,
                labelStyle: {
                  fontSize: 12,
                },
              },
              noDataOverlay: { message: t('common:customDashboard.noData') },
            }}
            grid={{
              vertical: displayGlobalState.showGridLines,
              horizontal: displayGlobalState.showGridLines,
            }}
            slots={{
              mark: displayState.showLabels ? MarkWithLabel : undefined,
              popper: CustomAxisTooltip,
            }}
            tooltip={{ trigger: 'axis' }}
          />
        )}
      </Grid>
    </Grid>
  );
};
